import React from 'react';
import { Card, Statistic, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { TodayFreshness } from '@/types';
import { freshnessColor } from './todayMath';

const { Text } = Typography;

const FRESHNESS_HINT: Record<TodayFreshness, string> = {
  ok: 'Updating normally',
  stale: 'No new events for the last 2 hours although yesterday had some',
  dead: 'Not updating',
};

export interface TodayPace {
  /** From pacePercent; null when yesterday has nothing to compare with. */
  percent: number | null;
  /** HH:MM IST, from the response. */
  byTime: string;
  /** Says what the pace is measured on when that is only part of the tile, e.g. "IAP". */
  scope?: string;
  /**
   * Set when today's or yesterday's numbers for this tile are partial: the
   * pace is shown as a neutral "— vs yesterday" with this as its tooltip.
   */
  hiddenReason?: string | null;
}

export interface TodayTileProps {
  label: string;
  /** Already formatted. */
  value: string;
  size: 'large' | 'small';
  freshness?: TodayFreshness;
  /** Overrides the dot's tooltip for some states, e.g. for a tile not fed by the counters. */
  freshnessHints?: Partial<Record<TodayFreshness, string>>;
  pace?: TodayPace;
  /** A line under the value (and pace), e.g. "peak 6,140 at 13:02". */
  detail?: React.ReactNode;
  /** HH:MM; when set, shows the "Counting since" badge. */
  countingSince?: string | null;
  /** Content of the (i) tooltip next to the label. */
  info?: React.ReactNode;
  /** The mini chart (large tiles only). */
  chart?: React.ReactNode;
}

const PaceLine: React.FC<{ pace: TodayPace }> = ({ pace }) => {
  const prefix = pace.scope ? `${pace.scope} ` : '';
  const { percent, hiddenReason } = pace;
  if (hiddenReason) {
    return (
      <Tooltip title={hiddenReason}>
        <Text type="secondary" style={{ fontSize: 12 }} data-pace-note={hiddenReason}>
          {prefix}— vs yesterday
        </Text>
      </Tooltip>
    );
  }
  if (percent === null) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {prefix}— vs yesterday
      </Text>
    );
  }
  const text = `${Math.abs(percent)}% vs yesterday by ${pace.byTime}`;
  if (percent === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {prefix}● {text}
      </Text>
    );
  }
  return (
    <span className={percent > 0 ? 'trend-up' : 'trend-down'} style={{ fontSize: 12 }}>
      {prefix}
      {percent > 0 ? '▲' : '▼'} {text}
    </span>
  );
};

/** One number of the Today board: label with freshness dot, value, pace, detail, optional chart. */
export const TodayTile: React.FC<TodayTileProps> = ({
  label,
  value,
  size,
  freshness,
  freshnessHints,
  pace,
  detail,
  countingSince,
  info,
  chart,
}) => {
  const large = size === 'large';
  const hint = freshness ? (freshnessHints?.[freshness] ?? FRESHNESS_HINT[freshness]) : '';
  const title = (
    <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {freshness && (
        <Tooltip title={hint}>
          <span
            data-testid="freshness-dot"
            data-freshness={freshness}
            role="img"
            aria-label={hint}
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              marginRight: 6,
              background: freshnessColor(freshness),
            }}
          />
        </Tooltip>
      )}
      {label}
      {info && (
        <Tooltip title={info}>
          <InfoCircleOutlined style={{ color: '#8c8c8c', marginLeft: 6 }} />
        </Tooltip>
      )}
    </span>
  );

  return (
    <Card
      className="stat-card"
      size="small"
      style={{ height: '100%' }}
      styles={{ body: { padding: '12px 14px' } }}
    >
      <Statistic
        title={title}
        value={value}
        formatter={() => value}
        valueStyle={{
          fontSize: large ? 26 : 18,
          fontWeight: large ? 700 : 650,
          fontVariantNumeric: 'tabular-nums',
        }}
      />
      {pace && (
        <div>
          <PaceLine pace={pace} />
        </div>
      )}
      {detail && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {detail}
          </Text>
        </div>
      )}
      {countingSince && (
        <Tooltip title="The live counters started at this time (IST); earlier hours of today are not in this number.">
          <Tag style={{ marginTop: 6, marginInlineEnd: 0, fontSize: 11 }}>
            Counting since {countingSince}
          </Tag>
        </Tooltip>
      )}
      {large && chart}
    </Card>
  );
};
